import * as ser from '../Common/SerUtil.js'
import { ComponentArray } from '../Common/ComponentArray.js'
import { makeEnum, makeEnumWithData, makeOptions,
  makeEnumWithDataAndLabels,
  setupClass, lookupData, Matches,
  interpolateInMap, doubleInterpolateInMap,
  IdsMap, PleaseContactStr,
  IntervalTimer,
  ResultsState,
} from '../Base.js'
import { WindowsData, } from '../MaterialData/Windows/WindowsData.js' 
import { CalcContext } from '../Common/CalcContext.js'
import { TypesSet } from './TypesSet.js'
import { GuideState } from '../BuildingComponents/GuideState.js'

import { Project, ProjectTypes, } from '../Project.js'
import { OutputValue } from '../Common/OutputValue.js'

import { Quantity } from '../Common/Quantity.js'
import { Units } from '../Common/Units.js'
export { Units } from '../Common/Units.js'

import {
  FieldType,
  kFieldTypesData,
  FieldInputType,
  Field,
  FieldGroup,
  ProjectUnits,
} from '../Common/Field.js'
export * from '../Common/Field.js'

import { DoorType } from './DoorType.js'
import { ExteriorShadingType } from './ExteriorShadingType.js'
import { InteriorShadingType } from './InteriorShadingType.js'
import { WindowType } from './WindowType.js'
import { WallType, RoofType } from './WallType.js'
import { SkylightType } from './SkylightType.js'
import { BufferSpaceType } from './BufferSpaceType.js'
import { HouseDesignTempInputs } from './HouseDesignTemps.js'
import { HouseToplevelData } from './HouseToplevelData.js'
import { HouseMiscDetails } from './HouseMiscDetails.js'
import { HouseInternalsData } from './HouseInternalsData.js'
import { Wall } from './Wall.js'
import { Roof } from './Roof.js'
import { Partition } from './Partition.js'
import { Floor } from './Floor.js'
import { HouseVentilationInfiltration } from './HouseVentilationInfiltration.js'
import { ComponentsLibrary } from '../BuildingComponents/ComponentsLibrary.js'

import {
  ResultsNode,
  ResultsNodeType,
  LineChartNode,
  MultiLineChartNode,
  StackedBarChartNode,
  SunburstNode,
  SummaryTableNode,
} from '../BuildingComponents/ResultsTree.js'

import { gApp, DebugOn } from '../Globals.js'

export class ResidentialResults {
  constructor(results, projectErrors) {
    // Of the form {inputs, outputs}
    this.results = results;
    this.projectErrors = projectErrors;
    this.calcContext = null;

    // Note: we don't store this in the project, but it gets serialized for
    // unit tests and also for PDF generation.
    this.serFields = [
      ser.genericObjectField('results', [OutputValue, ResidentialResultsNode]),
      'projectErrors',
    ]
  }

  getProjectName() {
    return this.results.info.projectName;
  }

  getLocationName() {
    return this.results.info.locationName;
  }

  getDateCalculatedOn() {
    return this.results.info.dateCalculatedOn;
  }

  getCalcContext() {
    return this.calcContext;
  }

  setCalcContext(ctx) {
    this.calcContext = ctx;
  }

  getInputs() {
    return this.results.inputs;
  }

  getOutputUnits() {
    return this.results.outputUnits;
  }

  getOutputs() {
    return this.results.outputs;
  }

  getSummaryNode() {
    return this.results.summaryNode;
  }

  getDetailsNode() {
    return this.results.detailsNode;
  }

  getError() {
    return this.results.error;
  }

  getProjectErrors() {
    return this.projectErrors;
  }

  noErrors() {
    return !this.projectErrors && !this.results.error;
  }
}

export class HouseUserGuideState extends GuideState {
  init() {
    super.init();
    this.steps = [
      {
        name: 'Build House',
        isReady: () => {
          return true;
        },
        pagePath: 'build',
      },
      {
        name: 'Run Calculations',
        isReady: () => {
          return true;
        },
        pagePath: 'results',
      },
    ]
  }
}
setupClass(HouseUserGuideState);

export class ResidentialProject extends Project {    
  init(id, units, opts) {
    super.init(id, units, opts);
  }

  setup() {
    /*
    Note: we do the init code in 'setup' because we must init and set gApp.proj() for 
    */
    this.type = ProjectTypes.Residential;
    this.toplevelData = HouseToplevelData.create();
    this.designTempInputs = HouseDesignTempInputs.create();
    // The house's walls/roofs/floors/partitions are created before
    // ventilationInfiltration and internals: their updaters read these arrays
    // (e.g. total exposed area), and Vue runs an updater immediately on creation.
    // Walls
    // We add objInfo property to the walls array so that the ErrorCollector
    // puts any walls errors under build/walls subpath and error highlighting works.
    this.walls = new ComponentArray(Wall);
    this.walls.objInfo = {
      _name: 'Walls',
      path: 'build/walls',
    }
    // Roofs
    this.roofs = new ComponentArray(Roof);
    this.roofs.objInfo = {
      _name: 'Roofs',
      path: 'build/roofs',
    }
    // Floors
    this.floors = new ComponentArray(Floor);
    this.floors.objInfo = {
      _name: 'Floors',
      path: 'build/floors',
    }
    // Partitions
    this.partitions = new ComponentArray(Partition);
    this.partitions.objInfo = {
      _name: 'Partitions',
      path: 'build/partitions',
    }

    this.ventilationInfiltration = HouseVentilationInfiltration.create(this);
    this.internals = HouseInternalsData.create(this);
    this.miscDetails = HouseMiscDetails.create();

    this.wallTypes = new ComponentArray(WallType);
    this.roofTypes = new ComponentArray(RoofType);
    this.windowTypes = new ComponentArray(WindowType);
    this.interiorShadingTypes = new ComponentArray(InteriorShadingType);
    this.exteriorShadingTypes = new ComponentArray(ExteriorShadingType);
    this.doorTypes = new ComponentArray(DoorType);
    this.skylightTypes = new ComponentArray(SkylightType);
    this.bufferSpaceTypes = new ComponentArray(BufferSpaceType);

    this.componentsLibrary = ComponentsLibrary.create();

    this.windowsData = new WindowsData();

    this.results = null;
    this.resultsState = ResultsState.None;

    this.guide = HouseUserGuideState.create(this);
  }

  getResults() {
    return this.results;
  }

  getResultsState() {
    return this.resultsState;
  }

  resultsReady() {
    return this.resultsState === ResultsState.Ready;
  }

  get serFields() {
    return [
      ...super.serFields,
      'toplevelData',
      'designTempInputs',
      'ventilationInfiltration',
      'internals',
      'miscDetails',
      'guide',
      ser.arrayField('walls', () => { return Wall.create(); }),
      ser.arrayField('roofs', () => { return Roof.create(); }),
      ser.arrayField('floors', () => { return Floor.create(); }),
      ser.arrayField('partitions', () => { return Partition.create(); }),
      ser.arrayField('wallTypes', () => { return WallType.create(); }),
      ser.arrayField('roofTypes', () => { return RoofType.create(); }),
      ser.arrayField('windowTypes', () => { return WindowType.create(); }),
      ser.arrayField('interiorShadingTypes', () => { return InteriorShadingType.create(); }),
      ser.arrayField('exteriorShadingTypes', () => { return ExteriorShadingType.create(); }),
      ser.arrayField('doorTypes', () => { return DoorType.create(); }),
      ser.arrayField('skylightTypes', () => { return SkylightType.create(); }),
      ser.arrayField('bufferSpaceTypes', () => { return BufferSpaceType.create(); }),
      'componentsLibrary',
    ]
  }

  async onSetupNewProject() {
    console.log("Setting up new Residential project...");

    this.shownWelcomeMessage = false;

    // Set the default value to Winnipeg MB, Canada
    // Do this here b/c we only want to do on project create
    await this.toplevelData.locationData.setLocation({
      fullName: 'Winnipeg, MB. Canada',
      name: 'Winnipeg',
      path: ['Canada', 'MB', 'Winnipeg'],
    });
  }

  async onPostReadFromJson() {
    console.log("Post read from JSON Residential project...");
    console.log("Reloading location data...");
    await this.toplevelData.reloadLocationData();
    for (const partition of this.partitions) {
      partition.migrateBufferTemperatures();
    }
  }

  async onCloseProject() {
    console.log("Closing Residential project...");
  }

  getBaseRoute() {
    return `/house/${this.id}`;
  }

  getLocationData() {
    return this.toplevelData.locationData;
  }

  getSampleTemps() {
    let locationData = this.toplevelData.locationData;
    return locationData.getSampleTemps({
      t_i_summer: this.toplevelData.indoorSummerTemp.value,
      t_i_winter: this.toplevelData.indoorWinterTemp.value,
    });
  }

  getFullTypesSet() {
    let typesSet = TypesSet.create();
    for (const wallType of this.wallTypes) {
      typesSet.addType(wallType, 'wallTypes');
    }
    for (const roofType of this.roofTypes) {
      typesSet.addType(roofType, 'roofTypes');
    }
    for (const windowType of this.windowTypes) {
      typesSet.addType(windowType, 'windowTypes');
    }
    for (const interiorShadingType of this.interiorShadingTypes) {
      typesSet.addType(interiorShadingType, 'interiorShadingTypes');
    }
    for (const exteriorShadingType of this.exteriorShadingTypes) {
      typesSet.addType(exteriorShadingType, 'exteriorShadingTypes');
    }
    for (const doorType of this.doorTypes) {
      typesSet.addType(doorType, 'doorTypes');
    }
    for (const skylightType of this.skylightTypes) {
      typesSet.addType(skylightType, 'skylightTypes');
    }
    for (const bufferSpaceType of this.bufferSpaceTypes) {
      typesSet.addType(bufferSpaceType, 'bufferSpaceTypes');
    }
    return typesSet;
  }

  getSpaceTypesSet() {
    let typesSet = TypesSet.create();
    for (const wall of this.walls) {
      typesSet.addTypes(wall.getTypesSet());
    }
    for (const roof of this.roofs) {
      typesSet.addTypes(roof.getTypesSet());
    }
    for (const partition of this.partitions) {
      typesSet.addTypes(partition.getTypesSet());
    }
    for (const floor of this.floors) {
      typesSet.addTypes(floor.getTypesSet());
    }
    return typesSet;
  }

  _getObjectsToIgnoreForErrors() {
    // We ignore any types that are not actually used in the house,
    // when we generate project errors.
    let allTypes = this.getFullTypesSet();
    //console.log("All types: ", allTypes.prettyLog());
    let houseTypes = this.getSpaceTypesSet();
    //console.log("House types: ", houseTypes.prettyLog());
    allTypes.removeTypes(houseTypes);
    //console.log("Ignored types: ", allTypes.prettyLog());
    return allTypes.toList();
  }

  updateResults() {
    console.log("Updating load results")
    if (this.resultsState == ResultsState.Calculating) {
      console.log("Already running calculations. Ignoring");
      return;
    }
    let projectErrorOpts = {
      ignoreSet: new Set(this._getObjectsToIgnoreForErrors()),
    }
    let projectErrors = this.updateProjectErrors(projectErrorOpts);
    if (!projectErrors.isEmpty()) {
      this.results = new ResidentialResults(null, projectErrors);
      this.resultsState = ResultsState.Ready;
      return;
    }
    this.resultsState = ResultsState.Calculating;
    let resultsObj = this._calcResults();
    this.results = new ResidentialResults({
      info: {
        projectName: this.getUiName(),
        locationName: this.toplevelData.locationData.locPath.fullName,
        dateCalculatedOn: this.getDateString(),
      },
      inputs: resultsObj.inputs,
      outputs: resultsObj.outputs,
      outputUnits: resultsObj.outputUnits,
      summaryNode: resultsObj.summaryNode,
      detailsNode: resultsObj.detailsNode,
      error: resultsObj.error,
    }, null);
    this.results.setCalcContext(resultsObj.calcContext);
    this.resultsState = ResultsState.Ready;
  }

  _calcRoofOutputs(ctx) {
    ctx.startSection("Roofs");
    
    let sumRoofHeating = 0;
    let sumRoofCooling = 0;
    let sumSkylightHeating = 0;
    let sumSkylightCooling = 0;
    ctx.roofDetails = [];
    let roofsBreakdown = {}
    let skylightsBreakdown = {}
    for (let i = 0; i < this.roofs.length; ++i) {
      let roof = this.roofs[i];
      ctx.startSection(`Roof${i + 1}`);
      let outputs = roof.calcOutputs(ctx, `Roof ${i + 1}`);
      ctx.endSection();
      ctx.roofDetails.push(outputs);

      sumRoofHeating += outputs.q_heating;
      sumRoofCooling += outputs.q_cooling;
      sumSkylightHeating += outputs.skylights.q_heating;
      sumSkylightCooling += outputs.skylights.q_cooling;

      let roofName = roof.getRoofType().name.value;
      roofsBreakdown[`Roof${i + 1}`] = {
        label: `Roof ${i + 1}: ${roofName}`,
        unit: Units.Load,
        value: {
          cooling: {
            sensible: outputs.q_cooling,
            latent: null,
          },
          heating: {
            sensible: outputs.q_heating,
            latent: null,
          },
        }
      }
      skylightsBreakdown = {
        ...skylightsBreakdown,
        ...outputs.skylights.Breakdown,
      }
    }

    ctx.res.roofs = {
      cooling: {
        sensible: sumRoofCooling,
        latent: null,
      },
      heating: {
        sensible: sumRoofHeating,
        latent: null,
      },
      Breakdown: roofsBreakdown,
    };
    ctx.res.skylights = {
      cooling: {
        sensible: sumSkylightCooling,
        latent: null,
      },
      heating: {
        sensible: sumSkylightHeating,
        latent: null,
      },
      Breakdown: skylightsBreakdown,
    };

    ctx.endSection();
  }

  _calcWallOutputs(ctx) {
    ctx.startSection("Walls");
    
    let sumWallHeating = 0;
    let sumWallCooling = 0;
    let sumWindowHeating = 0;
    let sumWindowCooling = 0;
    let sumDoorHeating = 0;
    let sumDoorCooling = 0;
    ctx.wallDetails = [];
    let wallsBreakdown = {}
    let windowsBreakdown = {}
    let doorsBreakdown = {}
    for (let i = 0; i < this.walls.length; ++i) {
      let wall = this.walls[i];
      ctx.startSection(`Wall${i + 1}`);
      let outputs = wall.calcOutputs(ctx, `Wall ${i + 1}`);
      ctx.endSection();
      ctx.wallDetails.push(outputs);

      sumWallHeating += outputs.q_heating;
      sumWallCooling += outputs.q_cooling;
      sumWindowHeating += outputs.windows.q_heating;
      sumWindowCooling += outputs.windows.q_cooling;
      sumDoorHeating += outputs.doors.q_heating;
      sumDoorCooling += outputs.doors.q_cooling;

      let wallName = wall.getWallType().name.value;
      wallsBreakdown[`Wall${i + 1}`] = {
        label: `Wall ${i + 1}: ${wallName}`,
        unit: Units.Load,
        value: {
          cooling: {
            sensible: outputs.q_cooling,
            latent: null,
          },
          heating: {
            sensible: outputs.q_heating,
            latent: null,
          },
        }
      }
      windowsBreakdown = {
        ...windowsBreakdown,
        ...outputs.windows.Breakdown,
      }
      doorsBreakdown = {
        ...doorsBreakdown,
        ...outputs.doors.Breakdown,
      }
    }

    ctx.res.walls = {
      cooling: {
        sensible: sumWallCooling,
        latent: null,
      },
      heating: {
        sensible: sumWallHeating,
        latent: null,
      },
      Breakdown: wallsBreakdown,
    };
    ctx.res.windows = {
      cooling: {
        sensible: sumWindowCooling,
        latent: null,
      },
      heating: {
        sensible: sumWindowHeating,
        latent: null,
      },
      Breakdown: windowsBreakdown,
    };
    ctx.res.doors = {
      cooling: {
        sensible: sumDoorCooling,
        latent: null,
      },
      heating: {
        sensible: sumDoorHeating,
        latent: null,
      },
      Breakdown: doorsBreakdown,
    };

    ctx.endSection();
  }

  _calcPartitionOutputs(ctx) {
    ctx.startSection("Partitions");
    
    let sumHeating = 0;
    let sumCooling = 0;
    let breakdown = {}
    for (let i = 0; i < this.partitions.length; ++i) {
      let partition = this.partitions[i];
      ctx.startSection(`Partition${i + 1}`);
      let outputs = partition.calcOutputs(ctx);
      ctx.endSection();

      sumHeating += outputs.q_heating;
      sumCooling += outputs.q_cooling;

      breakdown[`Partition${i + 1}`] = {
        label: `Partition ${i + 1}`,
        unit: Units.Load,
        value: {
          cooling: {
            sensible: outputs.q_cooling,
            latent: null,
          },
          heating: {
            sensible: outputs.q_heating,
            latent: null,
          },
        }
      }
    }

    ctx.res.partitions = {
      cooling: {
        sensible: sumCooling,
        latent: null,
      },
      heating: {
        sensible: sumHeating,
        latent: null,
      },
      Breakdown: breakdown,
    };

    ctx.endSection();
  }

  _calcFloorOutputs(ctx) {
    ctx.startSection("Floors");

    let sumHeating = 0;
    let sumCooling = 0;
    let breakdown = {}
    for (let i = 0; i < this.floors.length; ++i) {
      let floor = this.floors[i];
      ctx.startSection(`Floor${i + 1}`)
      let res = floor.calcOutputs(ctx);
      sumHeating += res.q_heating;
      sumCooling += res.q_cooling;
      breakdown[`Floor${i + 1}`] = {
        label: `Floor ${i + 1}`,
        unit: Units.Load,
        value: {
          cooling: {
            sensible: res.q_cooling,
            latent: null,
          },
          heating: {
            sensible: res.q_heating,
            latent: null,
          },
        }
      }
      ctx.endSection();
    }

    ctx.res.floors = {
      cooling: {
        sensible: sumCooling,
        latent: null,
      },
      heating: {
        sensible: sumHeating,
        latent: null,
      },
      Breakdown: breakdown,
    };

    ctx.endSection();
  }

  static _calcLoadTotals(ctx, resName, sectionsDict) {
    ctx.pushMsg(`${resName} = Sum of...`)
    ctx.items = sectionsDict;
    let totals = {
      cooling: {sensible: 0, latent: 0},
      heating: {sensible: 0, latent: 0},
    }
    for (const key in sectionsDict) {
      let item = sectionsDict[key];
      totals.cooling.sensible += item.cooling.sensible || 0;
      totals.cooling.latent += item.cooling.latent || 0;
      totals.heating.sensible += item.heating.sensible || 0;
      totals.heating.latent += item.heating.latent || 0;
    }
    ctx[resName] = totals;
    return totals;
  }

  _calcTotalEnvelopeOutputs(ctx) {
    ctx.startSection("Total Envelope Outputs")
    ctx.res.totalEnvelope = ResidentialProject._calcLoadTotals(ctx, 'totalEnvelope', {
      'walls': ctx.res.walls,
      'roofs': ctx.res.roofs,
      'partitions': ctx.res.partitions,
      'floors': ctx.res.floors,
      'windows': ctx.res.windows,
      'skylights': ctx.res.skylights,
      'doors': ctx.res.doors,
    });
    ctx.endSection();
  }

  _calcTotalSpaceLoad(ctx) {
    ctx.startSection("Total Space Load")
    ctx.res.totalSpaceLoad = ResidentialProject._calcLoadTotals(ctx, 'totalSpaceLoad', {
      'totalEnvelope': ctx.res.totalEnvelope,
      'ventilationInfiltration': ctx.res.ventilationInfiltration,
      'internals': ctx.res.internals.total,
    })
    ctx.endSection()
  }

  _calcTotalCoilLoad(ctx) {
    ctx.startSection("Total Coil Load");
    ctx.res.totalCoilLoad = ResidentialProject._calcLoadTotals(ctx, 'totalCoilLoad', {
      'totalSpaceLoad': ctx.res.totalSpaceLoad,
      'distributionLoads': ctx.res.distributionLoads,
    })
    ctx.endSection();
  }

  _runCalculations() {
    let ctx = CalcContext.create();
    let error = null;
    ctx.res = {};
    try {
      this.toplevelData.calcOutputs(ctx);
      this.designTempInputs.calcOutputs(ctx);
      this.ventilationInfiltration.calcOutputs(ctx);
      this._calcRoofOutputs(ctx);
      this._calcWallOutputs(ctx);
      this._calcPartitionOutputs(ctx);
      this._calcFloorOutputs(ctx);
      this.internals.calcOutputs(ctx);
      this._calcTotalEnvelopeOutputs(ctx);
      this._calcTotalSpaceLoad(ctx);
      this.miscDetails.calcOutputs(ctx);
      this._calcTotalCoilLoad(ctx);
    } catch (err) {
      // console.log("Error while calculating outputs: ", err);
      ctx.logFatalError(err);
      error = err;
      // Report the error. Errors here should ideally never happen if all pre-checks pass.
      gApp.reportError(err, {
        projName: this.getName(),
      });
    }
    return {ctx, error};
  }

  _calcResults() {
    let results = {
      inputs: null,
      outputs: null,
      calcContext: null,
      error: null,
    };
    try {
      let toplevelData = this.toplevelData;
      let locationData = toplevelData.locationData;
      let miscDetails = this.miscDetails;
      let outdoorAir = this.ventilationInfiltration;
      let internals = this.internals;

      let makeLocationDataOutput = (field) => {
        return OutputValue.createSimple(field.name, field.type, field.getValue(),
          {
            min: field.min,
            max: field.max,
          });
      };

      results.inputs = {
        basics: {
          floorSpace: OutputValue.createFromField(toplevelData.totalFloorArea),
          averageCeilingHeight: OutputValue.createFromField(toplevelData.averageCeilingHeight),
          numberBedrooms: OutputValue.createFromField(toplevelData.numberBedrooms),
          numberStoreys: OutputValue.createFromField(toplevelData.numberStoreys),
          indoorSummerTemp: OutputValue.createFromField(toplevelData.indoorSummerTemp),
          indoorSummerHumidity: OutputValue.createFromField(toplevelData.indoorSummerHumidity),
          indoorWinterTemp: OutputValue.createFromField(toplevelData.indoorWinterTemp),
        },
        environment: {
          location: new OutputValue("Location", locationData.locPath ? locationData.locPath.fullName : 'Not set', null, null),
          // TODO - make specific to the design temps chosen
          heatingDryBulb: makeLocationDataOutput(locationData.heating99p6PerDryBulb),
          coolingDryBulb: makeLocationDataOutput(locationData.cooling0p4PerDryBulb),
          latitude: makeLocationDataOutput(locationData.latitude),
          longitude: makeLocationDataOutput(locationData.longitude),
          elevation: makeLocationDataOutput(locationData.elevation),
        },
        envelope: {
          totalWallArea: OutputValue.createSimple("Total Wall Area", FieldType.Area, this._calcTotalWallArea(), {allowMin: true}),
          totalRoofArea: OutputValue.createSimple("Total Roof Area", FieldType.Area, this._calcTotalRoofArea(), {allowMin: true}),
          totalWindowArea: OutputValue.createSimple("Total Window Area", FieldType.Area, this._calcTotalWindowArea(), {allowMin: true}),
          totalDoorArea: OutputValue.createSimple("Total Door Area", FieldType.Area, this._calcTotalDoorArea(), {allowMin: true}),
          totalPartitionArea: OutputValue.createSimple("Total Partition Area", FieldType.Area, this._calcTotalPartitionArea(), {allowMin: true}),
        },
        outdoorAir: {
          totalVentilation: OutputValue.createFromField(
            outdoorAir.entryMethod.value === 'Automatic'
              ? outdoorAir.outputVentilation
              : outdoorAir.totalVentilation
          ),
          summerInfiltration: OutputValue.createFromField(
            outdoorAir.infiltrationEntryMethod.value === 'Automatic'
              ? outdoorAir.outputSummerInfiltration
              : outdoorAir.infiltrationManual.getField('summerInfiltration')
          ),
          winterInfiltration: OutputValue.createFromField(
            outdoorAir.infiltrationEntryMethod.value === 'Automatic'
              ? outdoorAir.outputWinterInfiltration
              : outdoorAir.infiltrationManual.getField('winterInfiltration')
          ),
          continuousExhaust: OutputValue.createFromField(outdoorAir.continuousExhaust),
          //recoveryFlow: OutputValue.createFromField(outdoorAir.heatEnergyRecovery),
          recoveryType: OutputValue.createFromField(outdoorAir.recoveryType),
        },
        internals: {
          useStdMethod: OutputValue.createFromField(internals.useStdMethod),
          additionalOccupants: OutputValue.createFromField(internals.additionalOccupants),
          additionalSensibleLoads: OutputValue.createFromField(internals.additionalSensibleLoads),
          additionalLatentLoads: OutputValue.createFromField(internals.additionalLatentLoads),
          outputNumPeople: OutputValue.createFromField(internals.outputNumPeople),
          outputSensibleLoad: OutputValue.createFromField(internals.outputSensibleLoad),
          outputLatentLoad: OutputValue.createFromField(internals.outputLatentLoad),
        },
        system: {
          ductRunLocation: OutputValue.createFromField(miscDetails.ductRunLocation),
          typicalLeakageRateEntry: OutputValue.createFromField(miscDetails.typicalLeakageRateEntry),
          typicalLeakageRate: OutputValue.createFromField(miscDetails.typicalLeakageRate),
          ductInsulation: OutputValue.createFromField(miscDetails.ductInsulation),
          systemType: OutputValue.createFromField(miscDetails.systemType),
        },
      };

      let {ctx, error} = this._runCalculations();
      results.calcContext = ctx;
      if (error) {
        throw error;
      }

      let res = ctx.res;

      // Derive duct factors from stored results: distributionLoads = totalSpaceLoad * F_dl
      let F_dl_heating = res.totalSpaceLoad.heating.sensible != 0
        ? res.distributionLoads.heating.sensible / res.totalSpaceLoad.heating.sensible : 0;
      let F_dl_cooling = res.totalSpaceLoad.cooling.sensible != 0
        ? res.distributionLoads.cooling.sensible / res.totalSpaceLoad.cooling.sensible : 0;
      results.inputs.system.ductLossFactor = OutputValue.createSimple(
        'Duct loss factor (heating)', FieldType.Decimal, F_dl_heating);
      results.inputs.system.ductGainFactor = OutputValue.createSimple(
        'Duct gain factor (cooling)', FieldType.Decimal, F_dl_cooling);
      // console.log("Results: ", prettyJson(res));

      // Data for the loads breakdown table
      let outputsData = {
        ['Envelope Loads']: {
          Walls: res.walls,
          Roofs: res.roofs,
          Partitions: res.partitions,
          Floors: res.floors,
          Windows: res.windows,
          Skylights: res.skylights,
          Doors: res.doors,
          Total: res.totalEnvelope,
        },
        ['Outdoor Air Loads']: {
          ['Ventilation & Infiltration']: res.ventilationInfiltration,
        },
        ['Internal Loads']: {
          People: res.internals.people,
          Other: res.internals.other,
          Total: res.internals.total,
        },
        ['Final Results']: {
          ['Total Space Load']: res.totalSpaceLoad,
          ['Air distribution gains/losses']: res.distributionLoads,
          ['Total Coil Load']: res.totalCoilLoad,
        }
      };
      let targetOutputUnits = Quantity.getConvertedUnits(Units.Load);
      results.outputUnits = targetOutputUnits;
      results.outputs = this._convertLoadOutputsToUnits(outputsData, targetOutputUnits);
      //console.log(`Original outputs: `, outputsData);
      //console.log(`Converted outputs: `, results.outputs);

      results.CombinedBreakdown = {
        Walls: {
          label: 'Walls',
          unit: Units.Load,
          value: res.walls,
          children: res.walls.Breakdown,
        },
        Roofs: {
          label: 'Roofs',
          unit: Units.Load,
          value: res.roofs,
          children: res.roofs.Breakdown,
        },
        Partitions: {
          label: 'Partitions',
          unit: Units.Load,
          value: res.partitions,
          children: res.partitions.Breakdown,
        },
        Floors: {
          label: 'Floors',
          unit: Units.Load,
          value: res.floors,
          children: res.floors.Breakdown,
        },
        Windows: {
          label: 'Windows',
          unit: Units.Load,
          value: res.windows,
          children: res.windows.Breakdown,
        },  
        Skylights: {
          label: 'Skylights',
          unit: Units.Load,
          value: res.skylights,
          children: res.skylights.Breakdown,
        },
        Doors: {
          label: 'Doors',
          unit: Units.Load,
          value: res.doors,
          children: res.doors.Breakdown,
        },
        ['Ventilation & Infiltration']: {
          label: 'Ventilation & Infiltration',
          unit: Units.Load,
          value: res.ventilationInfiltration,
          children: res.ventilationInfiltration.Breakdown,
        },
        People: {
          label: 'Internal (People)',
          unit: Units.Load,
          value: res.internals.people,
          children: res.internals.people.Breakdown,
        },
        Other: {
          label: 'Internal (Other)',
          unit: Units.Load,
          value: res.internals.other,
          children: res.internals.other.Breakdown,
        },
        Distribution: {
          label: 'Distribution',
          unit: Units.Load,
          value: res.distributionLoads,
          children: res.distributionLoads.Breakdown,
        },
      }

      results.summaryNode = this._createSummaryNode(ctx);
      results.detailsNode = new ResidentialResultsNode(ctx, results.CombinedBreakdown);
      //console.log("Results: ", results);
    } catch (err) {
      console.error(`Error during output calc:\n${err}\n${err.stack}`);
      results.error = err;
      gApp.reportError(err);
    }

    return results;
  }

  _convertLoadOutputsToUnits(outputsData, targetUnits) {
    let convertValue = (value) => {
      if (value === null || value === undefined) {
        return null;
      }
      return Quantity.convertValue(value, Units.Load, targetUnits);
    };
    let convertedOutputs = {};
    for (const outputGroupName in outputsData) {
      let outputGroup = outputsData[outputGroupName];
      convertedOutputs[outputGroupName] = {};
      for (const outputName in outputGroup) {
        let output = outputGroup[outputName];
        let convertedOutput = {
          cooling: {
            sensible: convertValue(output.cooling.sensible),
            latent: convertValue(output.cooling.latent),
          },
          heating: {
            sensible: convertValue(output.heating.sensible),
            latent: convertValue(output.heating.latent),
          },
        };
        convertedOutputs[outputGroupName][outputName] = convertedOutput;
      }
    }
    return convertedOutputs;
  }

  _calcTotalWallArea() {
    let area = 0;
    for (const wall of this.walls) {
      area += wall.area.value;
    }
    return area;
  }

  _createSummaryNode(calcContext) {
    let calcResults = calcContext.res;
    let data ={
      title: 'Summary',
      rows: [
        {
          label: 'Total Cooling Load (Sensible)',
          value: calcResults.totalCoilLoad.cooling.sensible,
          units: Units.Load,
          precision: 0,
        },
        {
          label: 'Total Cooling Load (Latent)',
          value: calcResults.totalCoilLoad.cooling.latent,
          units: Units.Load,
          precision: 0,
        },
        {
          label: 'Total Heating Load (Sensible)',
          value: calcResults.totalCoilLoad.heating.sensible,
          units: Units.Load,
          precision: 0,
        },
        {
          label: 'Cooling Design Temp.',
          value: calcContext.designTemps.coolingStr,
          units: Units.None,
        },
        {
          label: 'Heating Design Temp.',
          value: calcContext.designTemps.heatingStr,
          units: Units.None,
        },
        {
          label: 'Total Floor Area',
          value: this.toplevelData.totalFloorArea.value,
          units: Units.ft2,
          precision: 0,
        }
      ]
    }
    return new SummaryTableNode(data);
  }

  _calcTotalRoofArea() {
    let area = 0;
    for (const roof of this.roofs) {
      area += roof.area.value;
    }
    return area;
  }

  _calcTotalWindowArea() {
    let area = 0;
    for (const wall of this.walls) {
      for (const wallWindow of wall.windows) {
        area += wallWindow.getWindowType().getArea() * wallWindow.quantity.value;
      }
    }
    return area;
  }

  _calcTotalDoorArea() {
    let area = 0;
    for (const wall of this.walls) {
      for (const wallDoor of wall.doors) {
        area += wallDoor.getDoorType().getArea() * wallDoor.quantity.value;
      }
    }
    return area;
  }

  _calcTotalPartitionArea() {
    let area = 0;
    for (const partition of this.partitions) {
      area += partition.size.value;
    }
    return area;
  }
};
setupClass(ResidentialProject)

export class ResidentialResultsNode extends ResultsNode {
  static serInfo = {
    type: 'ResidentialResultsNode',
    ctor: () => { return new ResidentialResultsNode(null, null); },
  }

  constructor(ctx, combinedBreakdown) {
    super(ResultsNodeType.VerticalSplit, 'Details');
    this.ctx = ctx;
    this.combinedBreakdown = combinedBreakdown;

    this.title = "Details";

    if (ctx) {
      let coolingNode = this.createCoolingDetailsNode();
      this.children.push(coolingNode);

      let heatingNode = this.createHeatingDetailsNode();
      this.children.push(heatingNode);
    }

    this.serFields = [
      ser.staticField('_type', 'ResidentialResultsNode'),
      ...this.serFields,
    ]
  }

  getCoolingNode() {
    return this.children[0];
  }

  getHeatingNode() {
    return this.children[1];
  }

  createHeatingDetailsNode() {
    //console.log("Combined breakdown: ", this.results.CombinedBreakdown);
    let sensibleHeatingBreakdown = this.filterBreakdownRecursive(this.combinedBreakdown, (value) => {
      if (value?.heating?.sensible === undefined) {
        return null;
      }
      return value.heating.sensible;
    });
    let calcResults = this.ctx.res;
    sensibleHeatingBreakdown = {
      sensibleHeating: {
        label: 'Sensible Heating Loads',
        unit: Units.Load,
        precision: 0,
        value: calcResults.totalCoilLoad.heating.sensible,
        children: sensibleHeatingBreakdown,
      }
    }
    let sunburstData = {
      title: 'Sensible Heating Loads',
      units: Units.Load,
      precision:  0,
      includeTable: true,
      data: sensibleHeatingBreakdown,
    }
    let node = new SunburstNode(sunburstData);
    node.data.helpText = `This chart shows the breakdown of the sensible heating loads.`;
    return node;
  }

  createCoolingDetailsNode() {
    let sensibleCoolingBreakdown = this.filterBreakdownRecursive(this.combinedBreakdown, (value) => {
      if (value?.cooling?.sensible === undefined) {
        return null;
      }
      return value.cooling.sensible;
    }); 
    let calcResults = this.ctx.res;
    sensibleCoolingBreakdown = {
      sensibleCooling: {
        label: 'Sensible Cooling Loads',
        unit: Units.Load,
        precision: 0,
        value: calcResults.totalCoilLoad.cooling.sensible,
        children: sensibleCoolingBreakdown,
      }
    }
    let sunburstData = {
      title: 'Sensible Cooling Loads',
      units: Units.Load,
      precision: 0,
      includeTable: true,
      data: sensibleCoolingBreakdown,
    }
    let node = new SunburstNode(sunburstData);
    node.data.helpText = `This chart shows the breakdown of the sensible cooling loads.`;
    return node;
  }

  filterBreakdownRecursive(breakdown, filterFunc) {
    let filteredBreakdown = {};
    for (const key in breakdown) {
      let entry = breakdown[key];
      if (entry.value === undefined) {
        continue;
      }
      let filteredValue = filterFunc(entry.value);
      if (filteredValue === null) {
        continue;
      }
      let filteredEntry = {};
      filteredEntry.label = entry.label;
      filteredEntry.unit = entry.unit;
      filteredEntry.value = filteredValue;
      if (entry.children) {
        filteredEntry.children = this.filterBreakdownRecursive(entry.children, filterFunc);
      }
      filteredBreakdown[key] = filteredEntry;
    }
    return filteredBreakdown;
  }
}